import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { Component as SmoothScrollLink } from '../SmoothScrollLink';
import { Component as Stars } from '../Stars';

const Container = styled.div`
  margin-bottom: ${props => props.theme.spacing.moderate}px;
  color: currentColor;
  display: flex;
  gap: 4px;
`;

const StyledStars = styled(Stars)``;

const AverageRating = styled.span``;

const ReviewCount = styled.span`
  ${props => props.theme.paragraph.variants.paragraph5Uppercase.textStyles}
  color: ${props =>
    props.empty ? props.theme.colors.grey : props.theme.colors.gunmetal};
  display: flex;
  align-items: center;
`;

class ProductDetailStars extends React.PureComponent {
  static propTypes = {
    className: PropTypes.string,
    product: PropTypes.object,
    reviews: PropTypes.shape({
      counts: PropTypes.shape({
        // Average rating for all reviews
        averageReview: PropTypes.number,
        // Total number of reviews
        reviewCount: PropTypes.number,
      }),
      summary: PropTypes.array,
    }),
    /** Show the number of reviews: "(10 Reviews)" */
    showReviewCount: PropTypes.bool,
    /** Show the average rating as a decimal "0.0" */
    showAverageRating: PropTypes.bool,
    starSize: PropTypes.number,
  };

  static defaultProps = {
    showReviewCount: false,
    showAverageRating: false,
  };

  renderStars(isEmpty = false) {
    const { showReviewCount, showAverageRating, reviews } = this.props;

    if (isEmpty) {
      return (
        <>
          {showAverageRating ? (
            <AverageRating data-autotag="rating-reviews-section">
              0.0
            </AverageRating>
          ) : null}
          <StyledStars
            readonly
            initialRating={0}
            empty
            color="var(--colors-grey, #ededf0)"
            size={this.props.starSize}
          />
          <ReviewCount empty>
            {showAverageRating && '('}
            <FormattedMessage
              id="site_pdp.review_count_label_unavailable"
              defaultMessage="Reviews are currently unavailable."
            />
            {showAverageRating && ')'}
          </ReviewCount>
        </>
      );
    }

    if (!reviews) {
      return;
    }

    const { counts, summary } = reviews;
    if (!counts) {
      return;
    }

    const reviewCount = counts.reviewCount;
    const averageRating = summary.find(s => s.label === 'Overall');

    return (
      <>
        {showAverageRating ? (
          <AverageRating data-autotag="rating-reviews-section">
            {averageRating.average.toFixed(1)}
          </AverageRating>
        ) : null}
        <StyledStars
          readonly
          initialRating={averageRating.average}
          color="var(--colors-gunmetal, #664ede)"
          size={this.props.starSize}
        />
        {showReviewCount ? (
          <>
            <ReviewCount data-autotag="reviews-count">
              <span data-autotag="pdpProductReviews">
                (
                <FormattedMessage
                  id="site_pdp.review_count_label"
                  defaultMessage="{count} {count, plural, one {Review} other {Reviews}}"
                  values={{ count: reviewCount }}
                />
                )
              </span>
            </ReviewCount>
          </>
        ) : null}
      </>
    );
  }

  render() {
    const { className, reviews, scrollTo } = this.props;

    if (!reviews) {
      return (
        <Container className={className} empty>
          {this.renderStars(true)}
        </Container>
      );
    }

    const content = this.renderStars();

    if (scrollTo && reviews.counts.reviewCount > 0) {
      return (
        <SmoothScrollLink>
          <Container
            className={className}
            as="a"
            href={scrollTo}
            data-autotag="stars-container"
          >
            {content}
          </Container>
        </SmoothScrollLink>
      );
    }

    return (
      <Container className={className} data-autotag="stars-container">
        {content}
      </Container>
    );
  }
}

ProductDetailStars.StyledStars = StyledStars;
ProductDetailStars.AverageRating = AverageRating;

export default ProductDetailStars;
